import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssessmentOutlined,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MarkdownContent from './MarkdownContent';

const InsightDetailDialog = ({
  insight,
  open,
  loading = false,
  loadError = '',
  freshnessLabel,
  onClose,
  onFeedback,
}) => {
  const { t, i18n } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState('');
  const [feedbackError, setFeedbackError] = useState('');

  useEffect(() => {
    setFeedback(insight?.feedback || '');
    setFeedbackError('');
    setSubmitting('');
  }, [insight]);

  if (!insight) return null;

  const methodLabel = t(`insights.methods.${insight.method}`);
  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';

  const submitFeedback = async (value) => {
    setSubmitting(value);
    setFeedbackError('');
    try {
      await onFeedback(insight, value);
      setFeedback(value);
    } catch (error) {
      setFeedbackError(error.message || t('insights.feedbackFailed'));
    } finally {
      setSubmitting('');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      fullScreen={false}
      aria-labelledby="insight-detail-title"
      aria-describedby="insight-detail-summary"
      PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle id="insight-detail-title" sx={{ pb: 1 }}>
        {insight.title || t('insights.untitled')}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} aria-live="polite">
            <CircularProgress size={20} aria-label={t('insights.detailLoadingAria')} />
            <Typography variant="body2" color="text.secondary">{t('insights.detailLoading')}</Typography>
          </Stack>
        )}
        {loadError && <Alert severity="warning" sx={{ mb: 2 }}>{loadError}</Alert>}
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip
            size="small"
            icon={<AssessmentOutlined />}
            label={methodLabel}
            aria-label={t('insights.methodAria', { method: methodLabel })}
          />
          {insight.confidence !== null && (
            <Chip
              size="small"
              variant="outlined"
              label={t('insights.confidenceValue', { confidence: Math.round(insight.confidence) })}
            />
          )}
          {freshnessLabel && <Chip size="small" variant="outlined" label={freshnessLabel} />}
        </Stack>

        <Box id="insight-detail-summary">
          <MarkdownContent variant="body1">{insight.summary}</MarkdownContent>
        </Box>

        {insight.detail && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 0.75 }}>
              {t('insights.explanation')}
            </Typography>
            <MarkdownContent>{insight.detail}</MarkdownContent>
          </Box>
        )}

        {insight.recommendation && (
          <Box sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, bgcolor: '#f4f8f4', borderLeft: '4px solid #2e7d32', borderRadius: 1 }}>
            <Typography variant="subtitle2" component="h3" sx={{ color: '#2e7d32', fontWeight: 700, mb: 0.5 }}>
              {t('insights.recommendedAction')}
            </Typography>
            <MarkdownContent>{insight.recommendation}</MarkdownContent>
          </Box>
        )}

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
          {t('insights.evidence')}
        </Typography>
        {insight.evidence.length ? (
          <List disablePadding aria-label={t('insights.evidenceListAria')}>
            {insight.evidence.map((item) => {
              const observed = item.observedAt && !Number.isNaN(new Date(item.observedAt).getTime())
                ? new Date(item.observedAt).toLocaleString(locale)
                : '';
              const secondaryParts = [item.source, observed].filter(Boolean).join(' · ');
              return (
                <ListItem key={item.id} disableGutters divider alignItems="flex-start">
                  <ListItemText
                    primary={item.label || item.value}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={(
                      <>
                        {item.label && item.value && <MarkdownContent>{String(item.value)}</MarkdownContent>}
                        {secondaryParts && <Typography variant="caption" color="text.secondary">{secondaryParts}</Typography>}
                      </>
                    )}
                  />
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {t('insights.noEvidence')}
          </Typography>
        )}

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('insights.feedbackQuestion')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant={feedback === 'helpful' ? 'contained' : 'outlined'}
            color="success"
            startIcon={submitting === 'helpful' ? <CircularProgress size={16} color="inherit" /> : <ThumbUpAltOutlined />}
            onClick={() => submitFeedback('helpful')}
            disabled={Boolean(submitting) || loading}
            aria-label={t('insights.helpfulAria')}
            aria-pressed={feedback === 'helpful'}
          >
            {t('insights.helpful')}
          </Button>
          <Button
            variant={feedback === 'not_helpful' ? 'contained' : 'outlined'}
            color="warning"
            startIcon={submitting === 'not_helpful' ? <CircularProgress size={16} color="inherit" /> : <ThumbDownAltOutlined />}
            onClick={() => submitFeedback('not_helpful')}
            disabled={Boolean(submitting) || loading}
            aria-label={t('insights.notHelpfulAria')}
            aria-pressed={feedback === 'not_helpful'}
          >
            {t('insights.notHelpful')}
          </Button>
        </Stack>
        {['helpful', 'not_helpful'].includes(feedback) && !feedbackError && (
          <Alert severity="success" sx={{ mt: 1.5 }}>{t('insights.feedbackSaved')}</Alert>
        )}
        {feedbackError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{feedbackError}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(submitting)} aria-label={t('common.close')}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InsightDetailDialog;
